import React from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { ListItem } from '@lmc/ui-kit'
import dayjs from 'dayjs'
import { NAMESPACE } from 'locale'
import { carePlansConstants } from 'locale/carePlans'
import { shortCutBuilder } from 'screens/Residents/screens/ResidentConsentScreens/utils'
import { ConsentStatus, STATUS_TRANSLATION_KEYS } from '@lmc/common/types'
import ConsentStatusTag from './ConsentStatusTag'
import { Carer } from '@lmc/ts-common'

export interface ConsentFormData {
  id: string
  title: string
  isArchived: boolean
  createdOn: string
  createdBy?: string
  isSigned?: boolean
  signedOn?: string
  signedBy?: string
  consentStatus: string
}

interface ConsentFormItemProps {
  data: ConsentFormData
  testId?: string
  onClick?: () => void
}

const formatDate = (dateString: string): string => {
  return dayjs(dateString).format('DD/MM/YYYY - HH:mm')
}

const ConsentFormItem: React.FC<ConsentFormItemProps> = ({ data, testId, onClick }) => {
  const { t } = useTranslation(NAMESPACE.CARE_PLANS)
  const carers: Carer[] = useSelector((state: any) => state.data.carers)

  const getCarerName = (carerId?: string): string => {
    if (!carerId) return ''
    const carer = carers?.find((c) => c.id === carerId)
    return carer ? `${carer.name.first} ${carer.name.last}` : ''
  }

  const isSigned = !!data.signedOn && !!data.signedBy
  const displayDate = isSigned ? formatDate(data.signedOn!) : formatDate(data.createdOn)
  const displayCarerName = isSigned ? getCarerName(data.signedBy) : getCarerName(data.createdBy)
  const translationKey = isSigned
    ? carePlansConstants.CONSENT_FORM_SIGNED_DATE
    : carePlansConstants.CONSENT_FORM_CREATED_DATE

  const iconContent = shortCutBuilder(data.title)

  const getStatusLabel = (status: string): string => {
    const statusTranslationKey = STATUS_TRANSLATION_KEYS[status as ConsentStatus]
    return statusTranslationKey ? t(statusTranslationKey) : status
  }

  const baseId = testId || 'consent-form-item'

  return (
    <StyledListItem
      titleContent={data.title}
      subTitleContent={t(translationKey, {
        date: displayDate,
        signedBy: displayCarerName,
        createdBy: displayCarerName
      })}
      iconContent={iconContent}
      hasBorder
      data-testid={baseId}
      onClick={onClick}
    >
      <TagsWrapper data-testid={`${baseId}-tags`}>
        <ConsentStatusTag
          status={data.consentStatus}
          label={getStatusLabel(data.consentStatus)}
        />
      </TagsWrapper>
    </StyledListItem>
  )
}

const StyledListItem = styled(ListItem)`
  height: 60px;
  padding-right: 8px;
  padding-left: 16px;
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.color.neutral.color9};
  }
`

const TagsWrapper = styled.div`
  display: flex;
  align-items: center;

  ${({ theme }) => theme.mediaQueries?.mobileDown} {
    flex-direction: column;
    align-items: center;
    gap: 5px;
  }
`

export default ConsentFormItem
